import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import {
  RefreshCw, Plus, X, Pencil, Trash2, Save,
  ChevronLeft, ChevronRight, Clock, Search,
  ClipboardCheck, GripVertical, List, Table, FilterX
} from 'lucide-react';

const OWNERS = ['claude', 'josh', 'danny', 'todd', 'jared'];
const STATUS_OPTIONS = ['pending', 'in_progress', 'validate', 'retry', 'complete'];

const TaskLogPage = () => {
  // ── Shared ────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const currentUserSlug = currentUser?.email?.split('@')[0]?.split('.')[0]?.toLowerCase() || null;

  // ── List view ─────────────────────────────────────────────────────────────
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Add task modal ────────────────────────────────────────────────────────
  const [showAddTask, setShowAddTask] = useState(false);
  const [assigneeManuallySet, setAssigneeManuallySet] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '', source: 'Task Log', status: 'pending', owner: currentUserSlug, assignee: currentUserSlug });

  // ── Shared filters (drive both list and datasheet) ────────────────────────
  const [dsFilters, setDsFilters] = useState({ status: ['pending', 'in_progress', 'validate', 'retry'] });

  // ── Datasheet state ───────────────────────────────────────────────────────
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalTask, setDescModalTask] = useState(null);
  const [descLocal, setDescLocal] = useState({ details: '' });

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getTaskLogJson();
      setTasks(response.data.tasks || []);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'pending')     return 'stui-badge-status-pending';
    if (s === 'in_progress') return 'stui-badge-status-in-progress';
    if (s === 'validate')    return 'stui-badge-status-validate';
    if (s === 'retry')       return 'stui-badge-status-retry';
    if (s === 'complete')    return 'stui-badge-status-complete';
    return '';
  };

  const formatOwnerName = (owner) => {
    if (!owner) return 'Unassigned';
    return owner.charAt(0).toUpperCase() + owner.slice(1);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (!isNaN(d)) {
        return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
      }
    } catch {}
    return ts;
  };

  const getStatusCount = (status) => {
    if (!tasks.length) return 0;
    return tasks.filter(t => t.status?.toLowerCase() === status).length;
  };

  const fieldDirty = (field) => isEditing && editForm && selectedTask && editForm[field] !== selectedTask[field];

  const hasChanges = isEditing && editForm && selectedTask && (
    editForm.task !== selectedTask.task ||
    editForm.details !== selectedTask.details ||
    editForm.source !== selectedTask.source ||
    editForm.owner !== selectedTask.owner ||
    editForm.assignee !== selectedTask.assignee ||
    editForm.status !== selectedTask.status
  );

  // ── Filtered data — dsFilters drives both views ───────────────────────────
  const applyFilters = (rows) => {
    let result = [...rows];
    Object.entries(dsFilters).forEach(([key, vals]) => {
      if (vals?.length > 0) result = result.filter(t => vals.includes(t[key] || ''));
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.task?.toLowerCase().includes(term) ||
        t.details?.toLowerCase().includes(term) ||
        t.source?.toLowerCase().includes(term) ||
        t.owner?.toLowerCase().includes(term) ||
        t.assignee?.toLowerCase().includes(term)
      );
    }
    return result;
  };

  const filteredTasks = useMemo(() => applyFilters(tasks), [tasks, dsFilters, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const dsDisplayData = useMemo(() => {
    if (!searchTerm) return tasks;
    const term = searchTerm.toLowerCase();
    return tasks.filter(t =>
      t.task?.toLowerCase().includes(term) ||
      t.details?.toLowerCase().includes(term) ||
      t.source?.toLowerCase().includes(term) ||
      t.owner?.toLowerCase().includes(term) ||
      t.assignee?.toLowerCase().includes(term)
    );
  }, [tasks, searchTerm]);

  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = Object.values(dsFilters).some(f => f?.length > 0);
  const dsFilteredCount = useMemo(() => {
    return tasks.filter(row =>
      Object.entries(dsFilters).every(([k, vals]) => !vals || vals.length === 0 || vals.includes(row[k] || ''))
    ).length;
  }, [tasks, dsFilters]);

  const dsColumns = useMemo(() => [
    { key: 'id',       fieldName: 'id',       label: 'ID',       width: 60  },
    { key: 'task',     fieldName: 'task',     label: 'Task',     width: 460 },
    { key: 'status',   fieldName: 'status',   label: 'Status',   width: 120,
      formatter: (v) => v ? <span className={`${getStatusClass(v)}`}>{v}</span> : null },
    { key: 'owner',    fieldName: 'owner',    label: 'Owner',    width: 100,
      formatter: (v) => formatOwnerName(v) },
    { key: 'assignee', fieldName: 'assignee', label: 'Assignee', width: 100,
      formatter: (v) => formatOwnerName(v) },
    { key: 'source',   fieldName: 'source',   label: 'Source',   width: 150 },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  // Editable cell renderer for datasheet edit mode
  const renderDsCell = (row, col, displayValue) => {
    switch (col.key) {
      case 'task':
        return <input className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'task', e.target.value)} style={{ width: '100%' }} />;
      case 'status':
        return (
          <select className="stui-ds-cell-input" value={displayValue ?? 'pending'} onChange={e => handleDsEdit(row.id, 'status', e.target.value)}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        );
      case 'owner':
        return (
          <select className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'owner', e.target.value || null)}>
            <option value="">Unassigned</option>
            {OWNERS.filter(o => o !== 'claude').map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
          </select>
        );
      case 'assignee':
        return (
          <select className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'assignee', e.target.value || null)}>
            <option value="">Unassigned</option>
            {OWNERS.map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
          </select>
        );
      case 'source':
        return <input className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'source', e.target.value)} style={{ width: '100%' }} />;
      default:
        return displayValue;
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;
    try {
      setSaving(true);
      const res = await devFilesApi.createTask({
        task: newTask.task.trim(),
        status: newTask.status || 'pending',
        details: newTask.details?.trim() || null,
        source: newTask.source || 'Task Log',
        owner: newTask.owner || null,
        assignee: newTask.assignee || newTask.owner || null,
        claudeSummary: null
      });
      setNewTask({ task: '', details: '', source: 'Task Log', status: 'pending', owner: currentUserSlug, assignee: currentUserSlug });
      setAssigneeManuallySet(false);
      setShowAddTask(false);
      await loadTasks();
      setSelectedTask(res.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    if (!selectedTask) return;
    setEditForm({ ...selectedTask });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    if (!editForm?.task?.trim()) return;
    try {
      setSaving(true);
      await devFilesApi.updateTask(editForm.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = () => {
    if (!selectedTask) return;
    notify.confirm('Delete this task?', async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteTask(selectedTask.id);
        setSelectedTask(null);
        setIsEditing(false);
        setEditForm({});
        await loadTasks();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete task');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleStatusAdvance = async (task, newStatus) => {
    try {
      setSaving(true);
      await devFilesApi.updateTask(task.id, { ...task, status: newStatus });
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // ── Datasheet handlers ────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      for (const id of dirtyIds) {
        const original = tasks.find(t => t.id === parseInt(id));
        if (!original) continue;
        await devFilesApi.updateTask(original.id, { ...original, ...dsEdits[id] });
      }
      setDsEdits({});
      setDsEditMode(false);
      notify.success('Changes saved');
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openDescModal = (task, editMode) => {
    const merged = { ...task, ...(dsEdits[task.id] || {}) };
    setDescModalTask(task);
    setDescLocal({ details: merged.details || '' });
    setShowDescModal(true);
  };

  const handleDescApply = () => {
    if (!descModalTask) return;
    setDsEdits(prev => ({ ...prev, [descModalTask.id]: { ...(prev[descModalTask.id] || {}), ...descLocal } }));
    setShowDescModal(false);
  };

  const selectTask = (task) => {
    if (isEditing && hasChanges) {
      notify.confirm('Discard unsaved changes?', () => { cancelEditing(); setSelectedTask(task); });
    } else {
      cancelEditing();
      setSelectedTask(task);
    }
  };

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">

      {/* ── Header ── */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <ClipboardCheck size={20} />
          <h1>Task Log</h1>
          <div className="stui-module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="stui-module-header-meta">
            {tasks.length} tasks &bull; {getStatusCount('pending')} pending
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="stui-crud-btn stui-delete-btn" onClick={handleDeleteTask} disabled={saving} title="Delete"><Trash2 size={18} /></button>
                : <button className="stui-crud-btn" onClick={() => setShowAddTask(true)} title="Add Task"><Plus size={18} /></button>
              }
              <button
                className={`stui-crud-btn ${!selectedTask && !isEditing ? 'disabled' : ''}`}
                onClick={isEditing ? cancelEditing : startEditing}
                disabled={!selectedTask && !isEditing}
                title={isEditing ? 'Cancel' : 'Edit'}
              >
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button
                className={`stui-crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`}
                onClick={handleSave}
                disabled={!isEditing || saving || !hasChanges}
                title="Save"
              ><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="stui-crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter — always visible when filters active, both views */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{tasks.length}</span>
            </button>
          )}

          {/* Search — persists across views */}
          <div className="stui-search-wrapper">
            <Search size={14} className="stui-search-icon" />
            <input
              className="stui-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search tasks..."
            />
          </div>

          {/* 6-dot */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadTasks(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="stui-list-view">

        {/* ── List view ── */}
        {viewMode === 'list' && (
          <>
            {listCollapsed ? (
              <div className="stui-list-panel stui-collapsed">
                <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="stui-list-panel">
                <div className="stui-list-panel-header">
                  <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                </div>
                <div className="stui-list-scroll">
                  {filteredTasks.length > 0 ? filteredTasks.map(task => (
                    <button key={task.id} onClick={() => selectTask(task)} className={`stui-list-item ${selectedTask?.id === task.id ? 'selected' : ''}`}>
                      <span className="stui-list-item-primary">{task.task}</span>
                      <span className="stui-list-item-secondary">
                        <Clock size={12} /> {formatTimestamp(task.entered)}
                        {task.assignee && <> &bull; {formatOwnerName(task.assignee)}</>}
                      </span>
                    </button>
                  )) : <div className="stui-detail-empty">No tasks</div>}
                </div>
              </div>
            )}

            {/* Detail panel */}
            <div className="stui-detail-panel">
              {selectedTask ? (
                <div className="stui-detail-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                  {/* General Information — fixed height, no scroll */}
                  <div style={{ flexShrink: 0 }}>
                    <div className="stui-detail-grid">

                      <div className="stui-col-24">
                        <div className="stui-detail-section-header">General Information</div>
                      </div>

                      <div className="stui-field-group stui-col-24">
                        <label>Task</label>
                        {isEditing
                          ? <input type="text" className={`stui-field-input${fieldDirty('task') ? ' dirty' : ''}`} value={editForm.task || ''} onChange={e => setEditForm({ ...editForm, task: e.target.value })} autoFocus />
                          : <input type="text" className="stui-field-input" value={selectedTask.task} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Status</label>
                        {isEditing
                          ? <select className={`stui-field-input${fieldDirty('status') ? ' dirty' : ''}`} value={editForm.status || 'pending'} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                            </select>
                          : <span className={`${getStatusClass(selectedTask.status)}`}>{selectedTask.status}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Owner</label>
                        {isEditing
                          ? <select className={`stui-field-input${fieldDirty('owner') ? ' dirty' : ''}`} value={editForm.owner || ''} onChange={e => setEditForm({ ...editForm, owner: e.target.value || null })}>
                              <option value="">Unassigned</option>
                              {OWNERS.filter(o => o !== 'claude').map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
                            </select>
                          : <span className="stui-badge-owner">{formatOwnerName(selectedTask.owner)}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Assignee</label>
                        {isEditing
                          ? <select className={`stui-field-input${fieldDirty('assignee') ? ' dirty' : ''}`} value={editForm.assignee || ''} onChange={e => setEditForm({ ...editForm, assignee: e.target.value || null })}>
                              <option value="">Unassigned</option>
                              {OWNERS.map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
                            </select>
                          : <span className="stui-badge-owner">{formatOwnerName(selectedTask.assignee)}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-12">
                        <label>Source</label>
                        {isEditing
                          ? <input type="text" className={`stui-field-input${fieldDirty('source') ? ' dirty' : ''}`} value={editForm.source || ''} onChange={e => setEditForm({ ...editForm, source: e.target.value })} />
                          : <input type="text" className="stui-field-input" value={selectedTask.source || ''} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="stui-field-group stui-col-6">
                        <label>Entered</label>
                        <input type="text" className="stui-field-input" value={formatTimestamp(selectedTask.entered)} readOnly onChange={() => {}} />
                      </div>

                      <div className="stui-field-group stui-col-6">
                        <label>ID</label>
                        <input type="text" className="stui-field-input" value={selectedTask.id} readOnly onChange={() => {}} />
                      </div>

                    </div>
                  </div>

                  {/* Details — fills remaining space */}
                  <div className="stui-detail-fill">
                    <div className="stui-detail-section-header">Details</div>
                    {isEditing
                      ? <textarea className={`stui-field-input${fieldDirty('details') ? ' dirty' : ''}`} value={editForm.details || ''} onChange={e => setEditForm({ ...editForm, details: e.target.value })} style={{ flex: 1, resize: 'none', minHeight: 0 }} />
                      : <textarea className="stui-field-input" value={selectedTask.details || ''} readOnly onChange={() => {}} style={{ flex: 1, resize: 'none', minHeight: 0, overflowY: 'auto' }} />
                    }
                  </div>

                  {/* Claude Summary — conditional, fixed height below details */}
                  {selectedTask.claudeSummary && (
                    <div style={{ flexShrink: 0, padding: '0 var(--spacing-md) var(--spacing-sm)' }}>
                      <div className="stui-detail-section-header">Claude Summary</div>
                      <textarea className="stui-field-input" value={selectedTask.claudeSummary} readOnly rows={4} onChange={() => {}} style={{ resize: 'none', overflowY: 'auto', width: '100%' }} />
                    </div>
                  )}

                  {/* Sprint action buttons — fixed at bottom */}
                  {!isEditing && (
                    <div className="stui-action-bar">
                      {(selectedTask.status === 'pending' || selectedTask.status === 'retry') && (
                        <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'in_progress')} disabled={saving}>Start</button>
                      )}
                      {selectedTask.status === 'in_progress' && (
                        <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'validate')} disabled={saving}>Mark Validate</button>
                      )}
                      {selectedTask.status === 'validate' && (
                        <>
                          <button className="stui-btn stui-btn-secondary" onClick={() => handleStatusAdvance(selectedTask, 'retry')} disabled={saving}>Retry</button>
                          <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'complete')} disabled={saving}>Complete</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="stui-detail-empty"><p>Select a task to view details</p></div>
              )}
            </div>
          </>
        )}

        {/* ── Datasheet view ── */}
        {viewMode === 'datasheet' && (
          <div className="stui-datasheet-view">
            <FilterableDataGrid
              data={dsDisplayData}
              allData={tasks}
              columns={dsColumns}
              filters={dsFilters}
              setFilters={setDsFilters}
              editModeEnabled={dsEditMode}
              editedData={dsEdits}
              renderEditableCell={renderDsCell}
              onRowDoubleClick={(row) => openDescModal(row, dsEditMode)}
            />
          </div>
        )}
      </div>

      {/* ── Add Task Modal ── */}
      {showAddTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>New Task</h3>
              <button className="stui-modal-close" onClick={() => { setShowAddTask(false); setAssigneeManuallySet(false); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid">

                <div className="stui-col-24">
                  <div className="stui-detail-section-header">General Information</div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Task</label>
                  <input type="text" className="stui-field-input" value={newTask.task}
                    onChange={e => setNewTask({ ...newTask, task: e.target.value })}
                    placeholder="Short description of the task" autoFocus />
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Status</label>
                  <select className="stui-field-input" value={newTask.status}
                    onChange={e => setNewTask({ ...newTask, status: e.target.value })}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Owner</label>
                  <select className="stui-field-input" value={newTask.owner || ''}
                    onChange={e => {
                      const owner = e.target.value || null;
                      setNewTask(prev => ({ ...prev, owner, assignee: assigneeManuallySet ? prev.assignee : owner }));
                    }}>
                    <option value="">Unassigned</option>
                    {OWNERS.filter(o => o !== 'claude').map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
                  </select>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Assignee</label>
                  <select className="stui-field-input" value={newTask.assignee || ''}
                    onChange={e => { setAssigneeManuallySet(true); setNewTask({ ...newTask, assignee: e.target.value || null }); }}>
                    <option value="">Unassigned</option>
                    {OWNERS.map(o => <option key={o} value={o}>{formatOwnerName(o)}</option>)}
                  </select>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Source</label>
                  <input type="text" className="stui-field-input" value={newTask.source}
                    onChange={e => setNewTask({ ...newTask, source: e.target.value })}
                    placeholder="Where this task came from" />
                </div>

                <div className="stui-col-24">
                  <div className="stui-detail-section-header">Details</div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <textarea className="stui-field-input resizable" value={newTask.details}
                    onChange={e => setNewTask({ ...newTask, details: e.target.value })}
                    placeholder="Requirements, context, notes..." rows={6} />
                </div>

              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => { setShowAddTask(false); setAssigneeManuallySet(false); }}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddTask} disabled={saving || !newTask.task.trim()}>
                {saving ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Details popup (datasheet double-click) ── */}
      {showDescModal && descModalTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>Task Details — #{descModalTask.id} — {descModalTask.task}</h3>
              <button className="stui-modal-close" onClick={() => setShowDescModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-field-group">
                <label>Details</label>
                {dsEditMode ? (
                  <textarea className="stui-field-input resizable" value={descLocal.details} onChange={e => setDescLocal(p => ({ ...p, details: e.target.value }))} rows={16} autoFocus />
                ) : (
                  <textarea className="stui-field-input resizable" value={descLocal.details || ''} readOnly rows={16} onChange={() => {}} />
                )}
              </div>
            </div>
            {dsEditMode && (
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary" onClick={() => setShowDescModal(false)}>Cancel</button>
                <button className="stui-btn stui-btn-primary" onClick={handleDescApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default TaskLogPage;
